import { ArrowRight, PlayCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

type DemoHeroProps = {
  onPlaceholderClick: () => void;
};

const previewStats = [
  { label: "Candidates analyzed", value: "124" },
  { label: "Shortlisted", value: "18" },
  { label: "AI interviews completed", value: "7" },
];

const candidateRows = [
  { name: "Aarav Mehta", match: "92%", signal: "Strong legal drafting evidence" },
  { name: "Nisha Rao", match: "88%", signal: "High client communication score" },
  { name: "Dev Kapoor", match: "81%", signal: "Solid research background" },
];

export function DemoHero({ onPlaceholderClick }: DemoHeroProps) {
  return (
    <section className="relative overflow-hidden border-b border-slate-200 bg-[#f8fafc]">
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(245,158,11,0.10),rgba(248,250,252,0)_34%,rgba(2,132,199,0.08)_100%)]" />
      <div className="relative mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl gap-12 px-4 pb-16 pt-32 sm:px-6 sm:pt-28 lg:grid-cols-[0.92fr_1.08fr] lg:items-center lg:px-8 lg:pb-20 lg:pt-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-amber-900">
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
            theRecruit Demo Hub
          </div>

          <h1 className="mt-7 max-w-4xl font-space-grotesk text-4xl font-bold leading-[1.02] tracking-normal text-slate-950 sm:text-5xl lg:text-6xl">
            Find every theRecruit demo in one place.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-8 text-slate-700 sm:text-lg">
            Use this hub before or after a sales conversation to explore the recruiter workflow,
            AI interviews, candidate experience, decision workspace, and staffing agency use cases.
            Each demo shows how theRecruit connects resume screening to evidence-backed hiring
            decisions.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button
              asChild
              size="lg"
              className="h-12 bg-amber-500 px-5 text-slate-950 hover:bg-amber-400"
            >
              <a href="#demo-cards" data-demo-cta="recruiter-workflow">
                Browse demos
                <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </a>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="h-12 border-slate-300 bg-white px-5 text-slate-900 hover:bg-slate-50"
              onClick={onPlaceholderClick}
              data-demo-cta="watch-overview"
            >
              <PlayCircle className="mr-2 h-4 w-4" aria-hidden="true" />
              Watch 90-sec overview
            </Button>
          </div>

          <p className="mt-5 max-w-xl text-sm leading-6 text-slate-600">
            Current and upcoming walkthroughs for teams evaluating theRecruit and theResume
            together.
          </p>
        </div>

        <div className="relative">
          <div className="absolute -inset-4 rounded-[2rem] bg-slate-900/5 blur-3xl" />
          <div className="relative overflow-hidden rounded-sm border border-slate-300 bg-slate-950 text-slate-100 shadow-2xl shadow-slate-900/20">
            <div className="flex items-center justify-between border-b border-slate-800 bg-slate-900 px-5 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                  Hiring workspace
                </p>
                <p className="mt-1 font-space-grotesk text-xl font-semibold">
                  Paralegal Associate
                </p>
              </div>
              <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-200">
                Top candidate match: 92%
              </span>
            </div>

            <div className="grid gap-px bg-slate-800 sm:grid-cols-3">
              {previewStats.map((stat) => (
                <div key={stat.label} className="bg-slate-950 p-5">
                  <p className="font-space-grotesk text-3xl font-bold text-slate-50">
                    {stat.value}
                  </p>
                  <p className="mt-2 text-xs uppercase tracking-[0.14em] text-slate-400">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>

            <div className="grid gap-px bg-slate-800 lg:grid-cols-[1fr_0.72fr]">
              <div className="bg-slate-950 p-5">
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-200">Ranked shortlist</p>
                  <span className="text-xs text-slate-500">Evidence view</span>
                </div>
                <div className="space-y-3">
                  {candidateRows.map((row, index) => (
                    <div
                      key={row.name}
                      className="grid grid-cols-[auto_1fr_auto] gap-3 border border-slate-800 bg-slate-900/70 p-3"
                    >
                      <span className="flex h-7 w-7 items-center justify-center border border-slate-700 text-xs text-slate-400">
                        {index + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-100">{row.name}</p>
                        <p className="mt-1 truncate text-xs text-slate-400">{row.signal}</p>
                      </div>
                      <span className="text-sm font-bold text-amber-300">{row.match}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-950 p-5">
                <p className="text-sm font-semibold text-slate-200">Interview signal</p>
                <div className="mt-4 space-y-4">
                  <div>
                    <div className="mb-2 flex justify-between text-xs text-slate-400">
                      <span>Structured answer quality</span>
                      <span>86%</span>
                    </div>
                    <div className="h-2 bg-slate-800">
                      <div className="h-2 w-[86%] bg-sky-500" />
                    </div>
                  </div>
                  <div>
                    <div className="mb-2 flex justify-between text-xs text-slate-400">
                      <span>Communication clarity</span>
                      <span>91%</span>
                    </div>
                    <div className="h-2 bg-slate-800">
                      <div className="h-2 w-[91%] bg-emerald-500" />
                    </div>
                  </div>
                  <div className="border border-slate-800 bg-slate-900/80 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                      Transcript excerpt
                    </p>
                    <p className="mt-3 text-sm leading-6 text-slate-300">
                      "I would first verify jurisdiction, summarize the clause risk, and prepare
                      follow-up questions for counsel."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
